import { css, styled } from '@mui/material';
import { useTranslation } from '@kc-console/core';

import { StackDirection } from '../../constants/stack-direction';
import { useCascadeState } from '../../utils';
import { parseCheckedState } from '../Checkbox';
import { CheckboxItem } from '../CheckboxItem';
import { checkboxGroupClasses } from './theme';

import type { ChangeEvent } from 'react';
import type { CheckedState } from '../Checkbox';
import type { CheckboxItemProps } from '../CheckboxItem';
import type { CheckboxGroupOption, CheckboxGroupOwnerState, CheckboxGroupProps } from './types';

export function CheckboxGroup<V = unknown>(props: CheckboxGroupProps<V>) {
  const { t } = useTranslation();
  const {
    className,
    direction = StackDirection.Vertical,
    disabled: disabledProp,
    error: errorProp,
    id,
    name,
    onChange: onChangeProp,
    options,
    selectAllOption,
    style,
    value: valueProp,
  } = props;

  const ownerState: CheckboxGroupOwnerState = {
    ...props,
    direction,
  } as CheckboxGroupProps<unknown>;

  const [value, onChange] = useCascadeState({
    initialValue: [],
    value: valueProp,
    onChange: (value: V[], event: ChangeEvent<HTMLInputElement>) => {
      onChangeProp?.(event, value);
    },
  });

  const getOptionChecked = (optionValue: V) => {
    return value?.includes(optionValue) ?? false;
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>, optionValue: V, checked: boolean) => {
    const nextValue = checked ? [...value, optionValue] : value.filter(v => v !== optionValue);
    onChange(nextValue, event);
  };

  const handleSelectAll = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    const nextValue = checked ? options.map(option => option.value) : [];
    onChange(nextValue, event);
  };

  const renderOption = (
    option: Omit<CheckboxGroupOption<V>, 'value'>,
    key: string | number,
    optionName: string | undefined,
    checkedState: CheckedState,
    onChange: CheckboxItemProps['onChange'],
  ) => {
    const { disabled = disabledProp, error = errorProp, info, status, text } = option;

    return (
      <CheckboxGroupOptionItem
        disabled={disabled}
        error={error}
        info={info}
        key={key}
        name={optionName}
        onChange={onChange}
        status={status}
        text={text}
        ownerState={ownerState}
        {...parseCheckedState(checkedState)}
      />
    );
  };

  const renderSelectAllOption = () => {
    const {
      disabled = disabledProp,
      error = errorProp,
      text = t('ui:checkbox.select_all'),
      ...others
    } = typeof selectAllOption === 'object' ? selectAllOption : {};
    return renderOption(
      { disabled, error, text, ...others },
      'select-all-option',
      undefined,
      value.length
        ? options.every(option => value.includes(option.value))
          ? 'checked'
          : 'indeterminate'
        : 'unchecked',
      handleSelectAll,
    );
  };

  return (
    <CheckboxGroupRoot className={className} id={id} style={style} ownerState={ownerState}>
      {selectAllOption && renderSelectAllOption()}
      {options.map((option, optionIndex) => {
        const { value } = option;
        const checked = getOptionChecked(value);

        return renderOption(
          option,
          optionIndex,
          name,
          checked ? 'checked' : 'unchecked',
          (event, checked) => handleChange(event, value, checked),
        );
      })}
    </CheckboxGroupRoot>
  );
}

const CheckboxGroupRoot = styled('div', {
  target: checkboxGroupClasses.root,
})<{ ownerState: CheckboxGroupOwnerState }>(({ ownerState }) => {
  const flexDirection = StackDirection.toFlexDirection(
    ownerState.direction ?? StackDirection.Vertical,
  );
  return css`
    display: flex;
    flex-direction: ${flexDirection};
    gap: ${flexDirection === 'row' ? '20px' : '4px'};
  `;
});

const CheckboxGroupOptionItem = styled(CheckboxItem, {
  target: checkboxGroupClasses.option,
})<{ ownerState: CheckboxGroupOwnerState }>``;
